import { BTDropdown } from "@btnet/core-components";
import { BTMenu } from "@btnet/core-components/BTMenu";

import { BTIconSmile } from "commonComponents/btWrappers/BTIcon";
import { type ICollapsableControl } from "commonComponents/btWrappers/editor/core/EditorControlBar/common/CollapsableControlGroup/CollapsableControlGroup";
import { EditorControlButton } from "commonComponents/btWrappers/editor/core/EditorControlBar/common/EditorControlButton/EditorControlButton";
import { InputRecaptureHackMenuItem } from "commonComponents/btWrappers/editor/core/EditorControlBar/common/InputRecaptureHack/InputRecaptureHack";
import {
    emojiDictionary,
    type EmojiTypes,
} from "commonComponents/btWrappers/editor/core/extensions/custom/emoji/EmojiMappings";
import { type ExtensionName } from "commonComponents/btWrappers/editor/core/extensions/editorExtensionUtils";
import { type IEditor } from "commonComponents/btWrappers/editor/core/useBTEditor.types";

const extensionId = "emoji";
interface IEmojiOption {
    key: string;
    icon: JSX.Element;
    isActive: (editor: IEditor) => boolean;
    onClick: (editor: IEditor) => void;
    menuItemContent: JSX.Element | string;
}
const emojiOptions: IEmojiOption[] = [];
Object.keys(emojiDictionary).forEach((emojiKey) => {
    const emojiKeyTyped = emojiKey as keyof typeof emojiDictionary;
    const emoji = emojiDictionary[emojiKeyTyped];
    emojiOptions.push({
        key: emojiKey,
        icon: <BTIconSmile />,

        isActive: ({ tiptapEditor }) => !tiptapEditor.isActive(extensionId),
        onClick: ({ tiptapEditor }) => {
            tiptapEditor
                .chain()
                .focus()
                .insertEmoji(emojiKey as EmojiTypes)
                .run();
        },
        menuItemContent: <div className="flex flex-row justify-center grow">{emoji.unicode}</div>,
    });
});
const getEmojiMenuItems = (editor: IEditor) => {
    return emojiOptions.map((emojiOption) => (
        <InputRecaptureHackMenuItem
            menuComponent={BTMenu.Item}
            editor={editor}
            key={emojiOption.key}
            menuProps={{
                testid: emojiOption.key,
                title: emojiOption.key,
                onClick: () => emojiOption.onClick(editor),
                className: "text-center",
            }}
        >
            {emojiOption.menuItemContent}
        </InputRecaptureHackMenuItem>
    ));
};
export const emojiControl: ICollapsableControl = {
    key: extensionId,
    isControlSupported(supportedExtensions: Set<ExtensionName>) {
        return supportedExtensions.has(extensionId);
    },
    isControlEnabled({ tiptapEditor }) {
        return tiptapEditor.can().chain().focus().insertEmoji("smile").run();
    },
    expandedElement(editor, _supportedExtensions, dropdownPlacement) {
        return (
            <BTDropdown
                testid="emoji-control-dropdown"
                overlay={<BTMenu testid="emojiControlMenu">{getEmojiMenuItems(editor)}</BTMenu>}
                placement={dropdownPlacement}
            >
                <EditorControlButton
                    friendlyName="Emoji"
                    data-testid="emojiControl"
                    icon={<BTIconSmile />}
                    isToggled={editor.tiptapEditor.isActive(extensionId)}
                    dropdown
                />
            </BTDropdown>
        );
    },
    collapsedActiveKeys({ tiptapEditor }) {
        return tiptapEditor.isActive(extensionId) ? [extensionId] : [];
    },
    collapsedMenuItem(editor) {
        return (
            <InputRecaptureHackMenuItem
                editor={editor}
                menuComponent={BTMenu.SubMenu}
                menuProps={{
                    icon: <BTIconSmile />,
                    title: "Emoji",
                    testid: "emojiCollapsedMenuItem",
                }}
                key={this.key}
            >
                {getEmojiMenuItems(editor)}
            </InputRecaptureHackMenuItem>
        );
    },
};
export default emojiControl;
